import Chip from '@mui/material/Chip';
import { Link } from 'react-router-dom';
import { PageHero } from '@/components/PageHero';
import { NotifyForm } from '@/components/NotifyForm';
import { CapabilityMap } from '@/components/CapabilityMap';
import { features, STATUS_META } from '@/content/features';
import { useSeo } from '@/seo/useSeo';
import { Reveal } from '@/components/Reveal';

export function Platform(): JSX.Element {
  useSeo({
    title: 'Platform — every module, one console',
    description:
      'Helpdesk & ticketing, endpoint management & RMM, Microsoft 365 backup, security, CRM & sales, unified tenant management, compliance & QBR, and a client/staff portal — one multi-tenant Nexus platform.',
    path: '/platform',
    schema: {
      '@type': 'ItemList',
      itemListElement: features.map((f, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        url: `https://nexusrmm.ai/platform/${f.slug}`,
        name: f.title,
      })),
    },
  });
  return (
    <div>
      <PageHero
        eyebrow="The platform"
        title="One platform. One console. Every module talking to the others."
        subtitle="No stitched-together tools, no glue integrations that quietly break. Explore each module below — every one is part of the same tenant-isolated, AI-native platform."
      />

      <section className="pb-16 md:pb-24">
        <div className="container-content">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.slug} delay={(i % 3) * 80}>
                  <Link
                    to={`/platform/${f.slug}`}
                    className="group flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-6 no-underline transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50"
                  >
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light transition-transform duration-200 group-hover:scale-110">
                      <Icon fontSize="small" />
                    </div>
                    <h2 className="font-display text-[17px] font-semibold text-nexus-ink">{f.title}</h2>
                    <p className="mt-1.5 flex-1 text-sm text-white/65">{f.body}</p>
                    <div className="mt-4 flex items-center gap-2">
                      {f.badge && (
                        <Chip
                          label={f.badge}
                          size="small"
                          className="h-[22px] border border-nexus-purple/35 bg-transparent text-[10.5px] font-bold tracking-wide text-nexus-purple-light"
                        />
                      )}
                      <Chip
                        label={STATUS_META[f.status].grid}
                        size="small"
                        className="h-[22px] border border-white/15 bg-transparent text-[10.5px] font-semibold text-white/55"
                      />
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CapabilityMap />

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <Link
                to="/integrations"
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 no-underline transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50"
              >
                <h3 className="font-display text-[17px] font-semibold text-nexus-ink">
                  Already running Syncro, Jamf, Intune, or a network monitoring stack?
                </h3>
                <p className="mt-1.5 text-sm text-white/65">
                  See how existing tools fold into the same console instead of requiring a rip-and-replace.
                </p>
              </Link>
              <Link
                to="/compliance"
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 no-underline transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50"
              >
                <h3 className="font-display text-[17px] font-semibold text-nexus-ink">
                  Need to answer for SOC 2, HIPAA, or another framework?
                </h3>
                <p className="mt-1.5 text-sm text-white/65">
                  See what the compliance module tracks for the specific frameworks your clients answer to.
                </p>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <h2 className="font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Want to see where each module stands?
              </h2>
              <p className="mx-auto mt-3 max-w-[520px] text-white/70">
                The <Link to="/roadmap" className="text-nexus-purple-light no-underline hover:underline">roadmap</Link> tracks
                every module from Foundation to General Availability — no guessing what&apos;s shipped
                versus what&apos;s still being built.
              </p>
              <div className="mt-8 flex justify-center">
                <NotifyForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
